import React, { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { debounce } from "lodash";
import { makeStyles, useMediaQuery, useTheme } from "@material-ui/core";

import {
  DESKTOP_TOP_BAR_HEIGHT,
  MOBILE_TOP_BAR_HEIGHT,
} from "@hubcapp/constants";
import { ErrorGeneric } from "@hubcapp/views";

import { ExperimentBanner, NavBar, TopBar } from "./components";
import PrivateRoute from "../../PrivateRoute";

const SIDEBAR_WIDTH = 220;

type StyleProps = { stickyNav: boolean; scrollTop: number };

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: "64px",
    height: "100%",
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      paddingTop: "57px",
      position: "relative",
      overflow: "initial",
    },
  },
  topBar: {
    zIndex: 1000,
  },
  container: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
    height: "100%",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: SIDEBAR_WIDTH,
    },
  },
  navBar: {
    zIndex: 3,
    width: SIDEBAR_WIDTH,
    minHeight: "100%",
    flex: "0 0 auto",
    [theme.breakpoints.up("lg")]: {
      height: "100vh",
      paddingTop: ({ stickyNav, scrollTop }: StyleProps) =>
        stickyNav ? 0 : DESKTOP_TOP_BAR_HEIGHT - scrollTop,
      position: "fixed",
      transition: "padding-top 100ms",
      zIndex: 0,
      left: 0,
      top: 0,
    },
  },
  content: {
    background: theme.palette.background.dark,
    overflowY: "auto",
    flex: "1 1 auto",
    padding: 0,
    minHeight: `calc(100vh - ${MOBILE_TOP_BAR_HEIGHT}px)`,
    [theme.breakpoints.up("lg")]: {
      minHeight: `calc(100vh - ${DESKTOP_TOP_BAR_HEIGHT}px)`,
    },
  },
}));

const isBrowser = typeof window !== `undefined`;

const Dashboard: React.FC = ({ children }) => {
  const [stickyNav, setStickyNav] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [openNavBarMobile, setOpenNavBarMobile] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const classes = useStyles({ stickyNav, scrollTop });
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("xs"), {
    defaultMatches: true,
  });

  const handleScroll = () => {
    window.requestAnimationFrame(() => {
      const scrollTop = window.pageYOffset;
      if (scrollTop < DESKTOP_TOP_BAR_HEIGHT) {
        setScrollTop(scrollTop);
        setStickyNav(false);
      } else {
        setStickyNav(true);
      }
    });
  };

  useEffect(() => {
    if (isBrowser) {
      window.addEventListener("scroll", debounce(handleScroll, 50));
    }
  }, []);

  const handleNavBarMobileOpen = (): void => {
    setOpenNavBarMobile(true);
  };

  const handleNavBarMobileClose = (): void => {
    setOpenNavBarMobile(false);
  };

  const toggleBanner = () => setShowBanner(!showBanner);

  return (
    <PrivateRoute>
      <div className={classes.root}>
        {window?._env_?.DISPLAY_EXPERIMENT_BANNER === "true" &&
          isMobileScreen && (
            <ExperimentBanner
              closeBanner={toggleBanner}
              showBanner={showBanner}
            />
          )}
        <TopBar
          className={classes.topBar}
          onMobileNavOpen={handleNavBarMobileOpen}
          displayExperimentBanner={toggleBanner}
        />
        <div className={classes.container}>
          <NavBar
            className={classes.navBar}
            onMobileClose={handleNavBarMobileClose}
            openMobile={openNavBarMobile}
          />
          <main className={classes.content}>
            {window?._env_?.DISPLAY_EXPERIMENT_BANNER === "true" &&
              !isMobileScreen && (
                <ExperimentBanner
                  closeBanner={toggleBanner}
                  showBanner={showBanner}
                />
              )}
            <Sentry.ErrorBoundary
              fallback={
                <div style={{ flex: 1 }}>
                  <ErrorGeneric />
                </div>
              }
            >
              {children}
            </Sentry.ErrorBoundary>
          </main>
        </div>
      </div>
    </PrivateRoute>
  );
};

export default Dashboard;
